import { useState, useEffect, useCallback } from 'react'
import { ResponsiveBar } from '@nivo/bar'
import s from './OpportunityByCreationDate.scss'
import {
  makeReportsPostRequest,
  makeGetRequest,
  getReportsBaseUrl,
  makeReportsGetRequest
} from 'utils/api'
import { toTeams } from 'utils/serializers'
import { toBarChartData, toUsers, getColors, toYears } from './serializers'
import { useLoading } from 'hooks'
import { getCurrency, getToken } from 'utils/authService'
import MaterialTable from '@material-table/core'
import { NumericFormat } from 'react-number-format'
import { timezoneDate, currencyFormatter } from 'utils/helpers'
import isEmpty from 'lodash/isEmpty'
import Select from 'react-select'
import { FaInfoCircle, FaFileExcel, FaAngleRight, FaAngleDown, FaAngleUp } from 'react-icons/fa'
import { Card, CardBody, CardHeader, Collapse, UncontrolledTooltip } from 'reactstrap'
import currency from 'currency.js'
import { AddButton } from 'components/common/AddButton'
import { PaginatedSelect } from 'components/common/PaginatedSelect'
import { toCompaniesSelect } from 'components/Companies/serializers'
import { ExportCsv, ExportPdf } from '@material-table/exporters'
import cn from 'classnames'

const columns = [
  {
    title: 'Creation Date',
    field: 'createdDate',
    sorting: true,
    width: '200px',
    defaultSort: 'asc',
    render: row => (row.createdDate ? timezoneDate(row.createdDate) : null),
    exportTransformer: row => (row.createdDate ? timezoneDate(row.createdDate) : null)
  },
  {
    title: 'Bid Due Date',
    field: 'bidDueDate',
    sorting: true,
    width: '200px',
    render: row => (row.bidDueDate ? timezoneDate(row.bidDueDate) : null),
    exportTransformer: row => (row.bidDueDate ? timezoneDate(row.bidDueDate) : null)
  },
  {
    title: 'Bid Follow-up Date',
    field: 'bidFollowUpDate',
    sorting: true,
    width: '200px',
    render: row => (row.bidFollowUpDate ? timezoneDate(row.bidFollowUpDate) : null),
    exportTransformer: row => (row.bidFollowUpDate ? timezoneDate(row.bidFollowUpDate) : null)
  },
  {
    title: 'Quote #',
    field: 'quoteNumber',
    sorting: true,
    width: '200px'
  },
  {
    title: 'Project #',
    field: 'projectNumber',
    width: '200px',
    sorting: true
  },
  {
    title: 'Opp Status',
    field: 'status',
    width: '200px',
    sorting: true
  },
  {
    title: 'Opp Name',
    field: 'name',
    width: '200px',
    sorting: true,
    render: row => (
      <a href={`/opportunities/${row.id}`} target="_blank" rel="noreferrer">
        {row.name}
      </a>
    )
  },
  {
    title: 'Client(s)',
    field: 'companies',
    sorting: true,
    width: '200px',
    render: row => row.companies.join(', '),
    exportTransformer: row => row.companies.join(', ')
  },
  {
    title: 'Contact(s)',
    field: 'contacts',
    sorting: true,
    width: '400px',
    render: row => {
      return row.contacts
        .map(
          c =>
            `${c.company}, ${c.name}, ${c.phone ? c.phone + ', ' : ''}${
              c.cellPhone ? c.cellPhone + ', ' : ''
            }${c.email}`
        )
        .join(' | ')
    },
    exportTransformer: row => {
      return row.contacts
        .map(
          c =>
            `${c.company}, ${c.name}, ${c.phone ? c.phone + ', ' : ''}${
              c.cellPhone ? c.cellPhone + ', ' : ''
            }${c.email}`
        )
        .join(' | ')
    }
  },
  {
    title: 'Awarded To',
    field: 'awardedCompany',
    width: '200px',
    sorting: true
  },
  {
    title: 'Won Value',
    field: 'wonValue',
    width: '200px',
    sorting: true,
    render: row => (
      <NumericFormat
        thousandSeparator
        decimalScale={2}
        value={row.wonValue}
        prefix={getCurrency().symbol}
        displayType="text"
      />
    ),
    exportTransformer: row => currencyFormatter({ value: row.wonValue })
  },
  {
    title: 'Bid Value',
    field: 'value',
    width: '200px',
    sorting: true,
    render: row => (
      <NumericFormat
        thousandSeparator
        decimalScale={2}
        value={row.value}
        prefix={getCurrency().symbol}
        displayType="text"
      />
    ),
    exportTransformer: row => currencyFormatter({ value: row.value })
  },
  {
    title: 'Cost',
    field: 'cost',
    width: '200px',
    sorting: true,
    render: row => (
      <NumericFormat
        thousandSeparator
        decimalScale={2}
        value={row.cost}
        prefix={getCurrency().symbol}
        displayType="text"
      />
    ),
    exportTransformer: row => currencyFormatter({ value: row.cost })
  },
  {
    title: 'Profit',
    field: 'grossProfit',
    width: '200px',
    sorting: true,
    render: row => (
      <NumericFormat
        thousandSeparator
        decimalScale={2}
        value={row.grossProfit}
        prefix={getCurrency().symbol}
        displayType="text"
      />
    ),
    exportTransformer: row => currencyFormatter({ value: row.grossProfit })
  },
  {
    title: 'Team(s)',
    field: 'teams',
    width: '250px',
    sorting: true,
    render: row => row.teams?.join(', '),
    exportTransformer: row => row.teams?.join(', ')
  },
  {
    title: 'Opp Manager',
    field: 'manager',
    width: '200px',
    sorting: true
  }
]

const OpportunityByCreationDate = () => {
  const [pipelineInfo, setPipelineInfo] = useState(null)
  const [result, setResult] = useState([])
  const [users, setUsers] = useState([])
  const [userFilter, setUserFilter] = useState([])
  const [years, setYears] = useState([])
  const [prevYears, setPrevYears] = useState(years)
  const [yearFilter, setYearFilter] = useState(null)
  const [teamFilter, setTeamFilter] = useState([])
  const [teams, setTeams] = useState([])
  const [, withLoadingLocks] = useLoading()
  const [collapse, setCollapse] = useState('')
  const [clientFilter, setClientFilter] = useState([])
  const [url, setUrl] = useState('/v1/deals/dealCreationDatePerYear')
  const [collapsed, setCollapsed] = useState(false)
  const [groupedByWeek, setGroupedByWeek] = useState(false)

  useEffect(() => {
    ;(async () => {
      const { content } = await makeGetRequest('/v1/users')
      setUsers(toUsers(content))

      const { years } = await makeReportsGetRequest('/v1/deals/dealCreationDatePerYear/years')
      setYears(toYears(years))

      const teams = await makeGetRequest('/v1/teams')
      setTeams(toTeams(teams))
    })()
  }, [])

  if (prevYears !== years) {
    setPrevYears(years)
    setYearFilter(years[years.length - 1])
  }

  useEffect(() => {
    ;(async () => {
      if (!yearFilter) return
      const result = await withLoadingLocks(
        makeReportsPostRequest(url, {
          year: yearFilter?.value ?? new Date().getFullYear(),
          ...(userFilter && userFilter.length
            ? { userFilter: userFilter.map(user => user.value) }
            : {}),
          ...(teamFilter && teamFilter.length
            ? { teamFilter: teamFilter.map(user => user.value) }
            : {}),
          ...(clientFilter && clientFilter.length
            ? { clientFilter: clientFilter.map(user => user.value) }
            : {}),
          ...(groupedByWeek ? { groupedByWeek: true } : {})
        })
      )
      setResult(result)
      setPipelineInfo(toBarChartData(result))
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamFilter, userFilter, yearFilter, clientFilter, url, groupedByWeek])

  const onBtnExport = useCallback(() => {
    window.open(
      getReportsBaseUrl(
        `${url}/export?groupedByWeek=${groupedByWeek ? true : false}&year=${
          yearFilter.value
        }&userFilter=${userFilter.map(user => user.value).join()}&teamFilter=${teamFilter
          .map(user => user.value)
          .join()}&clientFilter=${clientFilter.map(user => user.value).join()}&token=${getToken()}`
      ),
      '_blank'
    )
  }, [clientFilter, groupedByWeek, teamFilter, url, userFilter, yearFilter])

  const toggleCollapse = month => {
    if (collapse !== month) {
      setCollapse(month)
    } else {
      setCollapse('')
    }
  }

  return (
    <>
      <h6 className={s.title}>Sales Reports &gt; YTD + Anual Created Opportunities</h6>
      <div className={s.filters}>
        <Select
          onChange={values => setYearFilter(values)}
          options={years}
          value={yearFilter}
          placeholder="Year to Date (YTD)"
          className={s.select}
        />
        <Select
          onChange={values => setUserFilter(values)}
          options={users}
          value={userFilter}
          placeholder="Select User(s)"
          className={s.userSelect}
          isMulti
        />
        <FaInfoCircle id="Type1" style={{ position: 'relative', top: 15, left: 5 }} />
        <UncontrolledTooltip placement="bottom" target="Type1">
          To see all opportunities, please remove all users from selection.
        </UncontrolledTooltip>
        <PaginatedSelect
          url="/v1/companies"
          value={clientFilter}
          className={s.userSelect}
          onChange={values => setClientFilter(values)}
          serializer={toCompaniesSelect}
          placeholder="Select Client(s)"
          isMulti
        />
        <FaInfoCircle id="Type3" style={{ position: 'relative', top: 15, left: 5 }} />
        <UncontrolledTooltip placement="bottom" target="Type3">
          To see all opportunities, please remove all clients from selection.
        </UncontrolledTooltip>
        <Select
          onChange={values => setTeamFilter(values)}
          options={teams}
          value={teamFilter}
          placeholder="Select Team(s)"
          className={s.userSelect}
          isMulti
        />
        <FaInfoCircle id="Type2" style={{ position: 'relative', top: 15, left: 5 }} />
        <UncontrolledTooltip placement="bottom" target="Type2">
          To see all opportunities, please remove all teams from selection.
        </UncontrolledTooltip>
      </div>
      <div style={{ display: 'flex', marginBottom: 30 }}>
        <p
          onClick={() => {
            setUrl('/v1/deals/dealCreationDate/all')
            setCollapsed(false)
            setGroupedByWeek(false)
          }}
          style={{
            fontWeight: 600,
            cursor: 'pointer',
            textDecoration: 'underline',
            marginRight: 20,
            position: 'relative',
            top: 5
          }}
          className={url === '/v1/deals/dealCreationDate/all' ? s.active : undefined}
        >
          All Data
        </p>
        <p
          onClick={() => {
            setUrl('/v1/deals/dealCreationDatePerYear')
            setGroupedByWeek(false)
          }}
          style={{
            fontWeight: 600,
            cursor: 'pointer',
            textDecoration: 'underline',
            marginRight: 20,
            position: 'relative',
            top: 5
          }}
          className={
            url === '/v1/deals/dealCreationDatePerYear' && !groupedByWeek ? s.active : undefined
          }
        >
          Grouped by Month
        </p>
        <p
          onClick={() => {
            setUrl('/v1/deals/dealCreationDatePerYear')
            setGroupedByWeek(true)
          }}
          style={{
            fontWeight: 600,
            cursor: 'pointer',
            textDecoration: 'underline',
            position: 'relative',
            top: 5
          }}
          className={groupedByWeek ? s.active : undefined}
        >
          Grouped by Week
        </p>
      </div>
      {!pipelineInfo ? (
        <div className={s.table}>
          <AddButton className={s.exportExcelNoMargins} onClick={onBtnExport}>
            <FaFileExcel className={s.excelIcon} />
            Export to Excel
          </AddButton>
          <MaterialTable
            title=""
            columns={columns}
            data={result}
            options={{
              tableLayout: 'fixed',
              sorting: true,
              headerStyle: {
                backgroundColor: '#eee',
                color: '#000',
                fontSize: '14px'
              },
              pageSize: 10,
              padding: 'dense',
              pageSizeOptions: [5, 10, 20, 50, { value: result?.length, label: 'All' }],
              emptyRowsWhenPaging: false,
              columnsButton: true,
              exportMenu: [
                {
                  label: 'Export PDF',
                  exportFunc: (cols, datas) => ExportPdf(cols, datas, 'YTD + Anual Submitted Bids')
                },
                {
                  label: 'Export CSV',
                  exportFunc: (cols, datas) => ExportCsv(cols, datas, 'YTD + Anual Submitted Bids')
                }
              ]
            }}
            style={{
              boxShadow: 'none',
              fontSize: '13px'
            }}
          />
        </div>
      ) : (
        <>
          {Object.keys(result).length > 0 && !isEmpty(pipelineInfo) ? (
            <>
              <h6 className={s.subtitle}>
                Total: {pipelineInfo?.totalCount ?? 0} opportunities &#8226; Won:{' '}
                {currency(pipelineInfo?.totalWonValue ?? 0, {
                  symbol: getCurrency().symbol
                }).format()}{' '}
                &#8226; Bid:{' '}
                {currency(pipelineInfo?.totalBidValue ?? 0, {
                  symbol: getCurrency().symbol
                }).format()}
              </h6>
              {!groupedByWeek && (
                <p
                  onClick={() => setCollapsed(!collapsed)}
                  style={{ fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}
                >
                  {collapsed ? 'Expand' : 'Collapse'} Graph{' '}
                  {collapsed ? <FaAngleUp /> : <FaAngleDown />}
                </p>
              )}
              {!groupedByWeek && (
                <div
                  className={cn(s.barChart, {
                    [s.collapsed]: collapsed
                  })}
                  style={{ height: pipelineInfo.barByValue.length * 60 + 'px', maxHeight: 800 }}
                >
                  <ResponsiveBar
                    colors={getColors}
                    data={pipelineInfo.barByValue}
                    keys={pipelineInfo.keys}
                    indexBy="month"
                    enableGridX
                    enableGridY
                    margin={{ top: 10, right: 100, bottom: 50, left: 100 }}
                    padding={0.2}
                    innerPadding={3}
                    groupMode="grouped"
                    borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                    axisTop={null}
                    axisRight={null}
                    axisBottom={{
                      tickSize: 5,
                      tickPadding: 5,
                      tickRotation: 0,
                      legendPosition: 'middle',
                      legendOffset: 32
                    }}
                    axisLeft={{
                      tickSize: 0,
                      tickPadding: 5,
                      tickRotation: 0,
                      legendPosition: 'middle',
                      legendOffset: -40,
                      format: value => currency(value, { symbol: getCurrency().symbol }).format()
                    }}
                    tooltip={({ id, value }) => (
                      <span>
                        {id}:{' '}
                        <strong>
                          <NumericFormat
                            thousandSeparator
                            decimalScale={2}
                            value={value}
                            prefix={getCurrency().symbol}
                            displayType="text"
                          />
                        </strong>
                      </span>
                    )}
                    enableLabel={false}
                    labelSkipWidth={12}
                    labelSkipHeight={9}
                    labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                    legends={[
                      {
                        dataFrom: 'keys',
                        anchor: 'bottom-right',
                        direction: 'column',
                        justify: false,
                        translateX: 120,
                        translateY: 0,
                        itemsSpacing: 14,
                        itemWidth: 100,
                        itemHeight: 20,
                        itemDirection: 'left-to-right',
                        itemOpacity: 0.85,
                        symbolSize: 20,
                        effects: [
                          {
                            on: 'hover',
                            style: {
                              itemOpacity: 1
                            }
                          }
                        ]
                      }
                    ]}
                    animate
                    motionStiffness={90}
                    motionDamping={15}
                  />
                </div>
              )}
              <div className={s.table}>
                <AddButton className={s.exportExcel} onClick={onBtnExport}>
                  <FaFileExcel className={s.excelIcon} />
                  Export to Excel
                </AddButton>
                {Object.keys(result).map(month => (
                  <Card className={s.card} key={month}>
                    <CardHeader onClick={() => toggleCollapse(month)} data-event={month}>
                      <span className={s.arrow}>
                        {collapse === month ? <FaAngleRight /> : <FaAngleDown />}
                      </span>
                      {month} ({result[month].count}) &#8226; Won:{' '}
                      {currency(result[month].totalWonValue, {
                        symbol: getCurrency().symbol
                      }).format()}{' '}
                      &#8226; Bid:{' '}
                      {currency(result[month].totalBidValue, {
                        symbol: getCurrency().symbol
                      }).format()}
                    </CardHeader>
                    <Collapse isOpen={collapse === month}>
                      <CardBody>
                        {collapse === month && (
                          <MaterialTable
                            title=""
                            key={month}
                            columns={columns}
                            data={result[month].deals}
                            options={{
                              tableLayout: 'fixed',
                              sorting: true,
                              headerStyle: {
                                backgroundColor: '#eee',
                                color: '#000',
                                fontSize: '14px'
                              },
                              pageSize: 10,
                              padding: 'dense',
                              pageSizeOptions: [
                                5,
                                10,
                                20,
                                50,
                                { value: result[month].deals?.length, label: 'All' }
                              ],
                              emptyRowsWhenPaging: false,
                              columnsButton: true,
                              exportMenu: [
                                {
                                  label: 'Export PDF',
                                  exportFunc: (cols, datas) =>
                                    ExportPdf(cols, datas, 'YTD + Anual Submitted Bids')
                                },
                                {
                                  label: 'Export CSV',
                                  exportFunc: (cols, datas) =>
                                    ExportCsv(cols, datas, 'YTD + Anual Submitted Bids')
                                }
                              ]
                            }}
                            style={{
                              boxShadow: 'none',
                              fontSize: '13px'
                            }}
                          />
                        )}
                      </CardBody>
                    </Collapse>
                  </Card>
                ))}
              </div>
            </>
          ) : (
            <p className={s.noResults}>No results to display. Try different filters.</p>
          )}
        </>
      )}
    </>
  )
}

export default OpportunityByCreationDate
